import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  grafanaDrilldownURL,
  statusTelemetryClient,
  type GrafanaDrilldown,
  type MapFreshness,
  type MapFreshnessState,
  StatusMapDeltaPayloadError,
  type StatusAlert,
  type StatusMapChanges,
  type StatusMapSnapshot,
  type StatusTelemetryClient
} from './statusTelemetryApi';
import {
  applyStatusMapChanges,
  StatusMapDeltaRecoveryError
} from './statusMapDelta';

export type MapTransportState = 'connecting' | 'live' | 'polling' | 'disconnected';

export const deriveFreshness = (
  freshness: MapFreshness,
  nowMs: number,
  disconnected = false
): MapFreshnessState => {
  if (disconnected) return 'disconnected';
  const timestamp = freshness.received_at || freshness.observed_at;
  const observedMs = timestamp ? Date.parse(timestamp) : Number.NaN;
  if (!Number.isFinite(observedMs)) return 'unavailable';
  const intervals = Math.max(0, nowMs - observedMs) / Math.max(1, freshness.expected_interval_seconds * 1000);
  if (intervals > 5) return 'unavailable';
  if (intervals > 2) return 'stale';
  return freshness.state === 'unavailable' ? 'unavailable' : freshness.state === 'stale' ? 'stale' : 'fresh';
};

export const deriveServerClockOffset = (serverTime: string, clientReceivedAtMs: number): number => {
  const serverTimeMs = Date.parse(serverTime);
  if (!Number.isFinite(serverTimeMs)) return 0;
  return serverTimeMs - clientReceivedAtMs;
};

export const deriveSnapshotServerClockOffset = (
  serverTime: string,
  clientReceivedAtMs: number,
  cacheAgeSeconds: number
): number =>
  deriveServerClockOffset(serverTime, clientReceivedAtMs) +
  Math.max(0, cacheAgeSeconds) * 1000;

export type ChangeDecision = 'noop' | 'apply' | 'reload';

const mapDeltaHasEntityChanges = (response: StatusMapChanges) =>
  Object.values(response.upserts).some(items => items.length > 0) ||
  Object.values(response.patches).some(items => items.length > 0) ||
  Object.values(response.removed).some(items => items.length > 0) ||
  Object.keys(response.freshness).length > 0 ||
  response.observability !== undefined ||
  response.alert_totals !== undefined;

export const decideMapChangeAction = (currentRevision: string, response: StatusMapChanges): ChangeDecision => {
  const current = Number(currentRevision);
  const base = Number(response.base_revision);
  const next = Number(response.revision);
  const latest = Number(response.current_revision);
  const earliest = Number(response.earliest_revision);
  if (
    response.reload_required ||
    !Number.isSafeInteger(current) ||
    !Number.isSafeInteger(base) ||
    !Number.isSafeInteger(next) ||
    !Number.isSafeInteger(latest)
  ) {
    return 'reload';
  }
  if (
    base !== current ||
    next < current ||
    latest < next ||
    earliest > current + 1 ||
    (response.has_more && next === current) ||
    (!response.has_more && next !== latest)
  ) {
    return 'reload';
  }
  if (
    (response.upserts.alerts.length > 0 || response.removed.alerts.length > 0) &&
    response.alert_totals === undefined
  ) {
    return 'reload';
  }
  if (next === current) return mapDeltaHasEntityChanges(response) ? 'reload' : 'noop';
  return 'apply';
};

const alertFromLocation = () =>
  typeof window === 'undefined' ? null : new URL(window.location.href).searchParams.get('alert_id');

export const useStatusTelemetryMapController = ({
  enabled = true,
  client = statusTelemetryClient,
  snapshotPollIntervalMs = 0
}: {
  enabled?: boolean;
  client?: StatusTelemetryClient;
  snapshotPollIntervalMs?: number;
} = {}) => {
  type SnapshotState = {
    snapshot: StatusMapSnapshot;
    serverClockOffsetMs: number;
  };
  const [snapshotState, setSnapshotState] = useState<SnapshotState | null>(null);
  const [transport, setTransport] = useState<MapTransportState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [focusedAlertID, setFocusedAlertID] = useState<string | null>(alertFromLocation);
  const revisionRef = useRef('0');
  const syncingRef = useRef(false);
  const syncPendingRef = useRef(false);
  const syncAbortRef = useRef<AbortController | null>(null);
  const snapshotStateRef = useRef<SnapshotState | null>(null);

  const loadSnapshot = useCallback(
    async (signal?: AbortSignal) => {
      const next = await client.fetchSnapshot(signal);
      if (signal?.aborted) return next;
      revisionRef.current = next.revision;
      const nextState = {
        snapshot: next,
        serverClockOffsetMs: deriveSnapshotServerClockOffset(
          next.server_time,
          Date.now(),
          next.cache_metadata.age_seconds
        )
      };
      snapshotStateRef.current = nextState;
      setSnapshotState(nextState);
      setError(null);
      return next;
    },
    [client]
  );

  const syncChanges = useCallback(async () => {
    if (syncingRef.current) {
      syncPendingRef.current = true;
      return;
    }
    syncingRef.current = true;
    const abort = new AbortController();
    syncAbortRef.current = abort;
    try {
      do {
        syncPendingRef.current = false;
        try {
          if (!snapshotStateRef.current) continue;
          let pages = 0;
          let hasMore = false;
          do {
            if (++pages > 100) {
              throw new StatusMapDeltaRecoveryError('Map delta pagination exceeded its bound');
            }
            const cursor = revisionRef.current;
            const changes = await client.fetchChanges(cursor, abort.signal);
            if (abort.signal.aborted) return;
            const decision = decideMapChangeAction(cursor, changes);
            if (decision === 'reload') {
              await loadSnapshot(abort.signal);
              hasMore = false;
              break;
            }
            if (decision === 'apply') {
              const current = snapshotStateRef.current;
              if (!current) {
                await loadSnapshot(abort.signal);
                hasMore = false;
                break;
              }
              const nextSnapshot = {
                ...applyStatusMapChanges(current.snapshot, changes),
                cache_metadata: { state: 'fresh' as const, age_seconds: 0 }
              };
              const nextState = {
                snapshot: nextSnapshot,
                serverClockOffsetMs: deriveServerClockOffset(changes.server_time, Date.now())
              };
              snapshotStateRef.current = nextState;
              revisionRef.current = nextSnapshot.revision;
              setSnapshotState(nextState);
            }
            hasMore = changes.has_more;
          } while (hasMore && !abort.signal.aborted);
          if (abort.signal.aborted) return;
          setError(null);
        } catch (cause) {
          if (abort.signal.aborted) return;
          if (
            cause instanceof StatusMapDeltaRecoveryError ||
            cause instanceof StatusMapDeltaPayloadError
          ) {
            try {
              await loadSnapshot(abort.signal);
              if (abort.signal.aborted) return;
              setError(null);
            } catch (recoveryCause) {
              if (abort.signal.aborted) return;
              setError(
                recoveryCause instanceof Error
                  ? recoveryCause.message
                  : 'Unable to recover the telemetry map'
              );
              setTransport('polling');
            }
            continue;
          }
          setError(cause instanceof Error ? cause.message : 'Unable to update the telemetry map');
          setTransport('polling');
        }
      } while (syncPendingRef.current);
    } finally {
      if (syncAbortRef.current === abort) {
        syncAbortRef.current = null;
      }
      if (abort.signal.aborted) {
        syncPendingRef.current = false;
      }
      syncingRef.current = false;
    }
  }, [client, loadSnapshot]);

  useEffect(() => {
    if (!enabled) {
      syncAbortRef.current?.abort();
      syncPendingRef.current = false;
    }
    return () => {
      syncAbortRef.current?.abort();
      syncPendingRef.current = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const abort = new AbortController();
    setTransport('connecting');
    void loadSnapshot(abort.signal)
      .then(() => {
        if (!abort.signal.aborted && snapshotPollIntervalMs > 0) setTransport('polling');
      })
      .catch(cause => {
        if (!abort.signal.aborted) {
          setError(cause instanceof Error ? cause.message : 'Unable to load the telemetry map');
          setTransport('disconnected');
        }
      });
    return () => abort.abort();
  }, [enabled, loadSnapshot, snapshotPollIntervalMs]);

  useEffect(() => {
    if (!enabled || snapshotPollIntervalMs > 0 || typeof EventSource === 'undefined') return;
    const events = new EventSource('/api/v1/status/map/events');
    const invalidate = () => void syncChanges();
    events.addEventListener('open', () => setTransport('live'));
    events.addEventListener('invalidate', invalidate);
    events.addEventListener('error', () => setTransport('polling'));
    return () => events.close();
  }, [enabled, snapshotPollIntervalMs, syncChanges]);

  useEffect(() => {
    if (!enabled) return;
    const clock = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(clock);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || snapshotPollIntervalMs > 0) return;
    const poll = window.setInterval(() => void syncChanges(), 15_000);
    return () => window.clearInterval(poll);
  }, [enabled, snapshotPollIntervalMs, syncChanges]);

  useEffect(() => {
    if (!enabled || snapshotPollIntervalMs <= 0) return;
    const interval = window.setInterval(() => {
      const abort = new AbortController();
      void loadSnapshot(abort.signal)
        .then(() => setTransport('polling'))
        .catch(cause => {
          setError(cause instanceof Error ? cause.message : 'Unable to refresh the telemetry map');
          setTransport('disconnected');
        });
    }, snapshotPollIntervalMs);
    return () => window.clearInterval(interval);
  }, [enabled, loadSnapshot, snapshotPollIntervalMs]);

  const freshness = useMemo(() => {
    if (!snapshotState) return null;
    const disconnected = transport === 'disconnected';
    const serverNowMs = nowMs + snapshotState.serverClockOffsetMs;
    const { snapshot } = snapshotState;
    return {
      inventory: deriveFreshness(snapshot.freshness.inventory, serverNowMs, disconnected),
      telemetry: deriveFreshness(snapshot.freshness.telemetry, serverNowMs, disconnected),
      alerting: deriveFreshness(snapshot.freshness.alerting, serverNowMs, disconnected)
    };
  }, [nowMs, snapshotState, transport]);

  const focusedAlert: StatusAlert | null = useMemo(
    () =>
      [
        ...(snapshotState?.snapshot.active_alerts ?? []),
        ...(snapshotState?.snapshot.unverified_alerts ?? [])
      ].find(alert => alert.alert_id === focusedAlertID) ?? null,
    [focusedAlertID, snapshotState]
  );

  const focusAlert = useCallback((alertID: string | null) => {
    setFocusedAlertID(alertID);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (alertID) url.searchParams.set('alert_id', alertID);
    else url.searchParams.delete('alert_id');
    window.history.replaceState(null, '', url);
  }, []);

  useEffect(() => {
    if (!focusedAlertID || !snapshotState) return;
    if (
      [...snapshotState.snapshot.active_alerts, ...snapshotState.snapshot.unverified_alerts].some(
        alert => alert.alert_id === focusedAlertID
      )
    ) return;
    setFocusedAlertID(null);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('alert_id');
    window.history.replaceState(null, '', url);
  }, [focusedAlertID, snapshotState]);

  const openGrafana = useCallback((target: GrafanaDrilldown) => {
    window.location.assign(grafanaDrilldownURL(target));
  }, []);

  return {
    snapshot: snapshotState?.snapshot ?? null,
    transport,
    error,
    freshness,
    telemetryEstimated: freshness?.telemetry !== 'fresh',
    alertTotals: snapshotState?.snapshot.alert_totals ?? null,
    confirmedAlerts: snapshotState?.snapshot.active_alerts ?? [],
    unverifiedAlerts: snapshotState?.snapshot.unverified_alerts ?? [],
    projectionCache: snapshotState?.snapshot.cache_metadata ?? null,
    alertStateCurrent: snapshotState?.snapshot.cache_metadata.state === 'fresh',
    focusedAlert,
    focusAlert,
    openGrafana,
    reload: loadSnapshot
  };
};
